/**
 * ReviewPresentationView — Feb 2026 · sprint 375 corrective.
 *
 * Quick Calc is no longer a separate panel. When enabled, 4 scratch columns
 * are appended INSIDE the workbook grid (right of source columns, before
 * Remarks). Users click a calc cell, type `=`, then click any workbook cell
 * — its A1 reference is inserted at the cursor. On Enter the formula is
 * evaluated by `formulaEngine.js` (safe hand-rolled parser, no `eval`).
 * Everything is ephemeral: exit Present, calc state wiped.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { WorkbookRenderer } from '../components/reviews/WorkbookRenderer';
import { MeetingNotesPanel } from '../components/reviews/MeetingNotesPanel';
import { PreviousActionsPanel } from '../components/reviews/PreviousActionsPanel';
import { RelatedFilesPanel, SideBySideRelatedView } from '../components/reviews/RelatedFilesPanel';
import { CompleteReviewDialog } from '../components/reviews/CompleteReviewDialog';
import { evaluate as evalFormula, colIndexToLetters } from '../components/reviews/formulaEngine';
import { formatCellDisplay } from '../components/reviews/formatCell';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Maximize2, Minimize2, X, Eye, EyeOff, Ruler,
  Calculator, MessageSquare, History, Paperclip, CheckCircle, MessageCircle, Trash2,
} from 'lucide-react';

const PANEL = { NONE: null, NOTES: 'notes', PREV_ACTIONS: 'prev', RELATED: 'related' };
const CALC_COLUMNS = 4;
const FMT_LABELS = { num: 'Number', cur: '₹', pct: '%' };

// ── Formatter ────────────────────────────────────────────────────────
function _indianGroup(intStr) {
  const neg = intStr.startsWith('-');
  const d = neg ? intStr.slice(1) : intStr;
  if (d.length <= 3) return (neg ? '-' : '') + d;
  const lastThree = d.slice(-3);
  const rest = d.slice(0, -3);
  return (neg ? '-' : '') + rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree;
}
function formatCalc(v, fmt) {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE';
  if (typeof v !== 'number' || !Number.isFinite(v)) return String(v);
  if (fmt === 'pct') {
    const rounded = Math.round(v * 100) / 100;
    return rounded.toString().replace(/\.?0+$/, '') + '%';
  }
  const abs = Math.abs(v);
  if (fmt === 'cur' || abs >= 1000) {
    return (fmt === 'cur' ? '₹' : '') + _indianGroup(String(Math.round(v)));
  }
  return (Math.round(v * 100) / 100).toString();
}

export default function ReviewPresentationView() {
  const { API, getHeaders } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();

  const [review, setReview] = useState(null);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [sheet, setSheet] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [showRemarks, setShowRemarks] = useState(true);
  const [showHighlights, setShowHighlights] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [session, setSession] = useState(null);
  const [panel, setPanel] = useState(PANEL.NONE);
  const [prevActionsCount, setPrevActionsCount] = useState(0);
  const [selection, setSelection] = useState({ ranges: [], cells: [], cols: [], anchor: null, focus: null });
  const [alongside, setAlongside] = useState(null);
  const [showComplete, setShowComplete] = useState(false);
  const [pendingContext, setPendingContext] = useState(null);

  // Quick Calc — inline columns
  const [calcEnabled, setCalcEnabled] = useState(false);
  const [calcCells, setCalcCells] = useState({});  // {"R{r}C{cIdx}": {formula, fmt}}
  const [activeCalcCell, setActiveCalcCell] = useState(null);   // {r, c}
  const [calcDraft, setCalcDraft] = useState('');

  // ── Loaders (identical to prior version) ─────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/reviews/${id}`, { headers: getHeaders() });
        if (!r.ok) throw new Error('Failed to load review');
        setReview(await r.json());
      } catch (e) { toast.error(String(e.message || e)); }
    })();
  }, [API, getHeaders, id]);

  const loadSheet = useCallback(async (idx) => {
    try {
      const r = await fetch(`${API}/reviews/${id}/sheets/${idx}`, { headers: getHeaders() });
      if (!r.ok) throw new Error();
      setSheet(await r.json());
    } catch { toast.error('Failed to load sheet'); }
  }, [API, getHeaders, id]);
  useEffect(() => { loadSheet(sheetIndex); }, [sheetIndex, loadSheet]);

  const loadAnnotations = useCallback(async () => {
    try {
      const r = await fetch(`${API}/reviews/${id}/annotations`, { headers: getHeaders() });
      if (r.ok) setAnnotations((await r.json()).items || []);
    } catch { /* silent */ }
  }, [API, getHeaders, id]);
  useEffect(() => { loadAnnotations(); }, [loadAnnotations]);

  const loadPrevActionsCount = useCallback(async () => {
    try {
      const r = await fetch(`${API}/reviews/${id}/previous-actions`, { headers: getHeaders() });
      if (r.ok) setPrevActionsCount(((await r.json()).items || []).length);
    } catch { /* silent */ }
  }, [API, getHeaders, id]);
  useEffect(() => { loadPrevActionsCount(); }, [loadPrevActionsCount]);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/reviews/${id}/meeting/start`, { method: 'POST', headers: getHeaders() });
        if (r.ok) setSession(await r.json());
      } catch { /* silent */ }
    })();
  }, [API, getHeaders, id]);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);
  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else document.documentElement.requestFullscreen().catch(() => toast.error('Fullscreen not permitted'));
  };
  const exit = () => navigate(-1);

  // ── Context builder for Discuss / Meeting Notes ──────────────────
  const buildContextLabel = useCallback((r, c) => {
    if (!sheet) return null;
    const cell = (sheet.cells || []).find(x => x.r === r && x.c === c);
    const rowLabelCell = (sheet.cells || []).find(x => x.r === r && x.c === 1);
    const headers = (sheet.cells || [])
      .filter(x => x.c === c && x.r < r && (x.type === 's' || typeof x.raw === 'string'))
      .map(x => String(x.raw || '').trim())
      .filter(Boolean);
    const columnLabel = headers.slice(-1)[0] || `C${c}`;
    const rowLabel = rowLabelCell?.raw ? String(rowLabelCell.raw).trim() : `R${r}`;
    return {
      label: `${rowLabel} · ${columnLabel}`,
      value: cell ? formatCellDisplay(cell.raw, cell.type, null, cell.display) : null,
      target_ref: `R${r}C${c}`,
    };
  }, [sheet]);
  const focus = selection?.focus;
  useEffect(() => {
    if (!focus || !sheet) return;
    const ctx = buildContextLabel(focus.r, focus.c);
    setPendingContext(ctx ? {
      sheet_index: sheetIndex, target_ref: ctx.target_ref,
      context_label: ctx.label, captured_value: ctx.value,
    } : null);
  }, [focus, sheet, sheetIndex, buildContextLabel]);

  // ── Cell lookup for formula engine ───────────────────────────────
  // Coordinate mapping:
  //   • source column c=1..N → A1 letters A..
  //   • calc column i=0..3   → letters starting at N+1
  // Row: identical to source row number.
  const sourceColCount = useMemo(() => sheet?.dims?.cols || 0, [sheet]);
  const calcColBase = sourceColCount;   // 0-based offset

  const cellLookup = useCallback((col, row) => {
    if (col < 1 || row < 1) return { raw: null, type: 'b' };
    // Calc column?
    if (col > sourceColCount) {
      const cIdx = col - calcColBase - 1;
      const key = `R${row}C${cIdx}`;
      const calc = calcCells[key];
      if (!calc) return { raw: null, type: 'b' };
      // Re-evaluate lazily. Circular guard handled by engine's visited set.
      const rv = evalFormula(calc.formula, cellLookup);
      return { raw: rv.error ? null : rv.value, type: rv.kind === 'number' ? 'n' : 's' };
    }
    // Source column: source_override wins.
    const overrideAnn = annotations.find(a =>
      a.sheet_index === sheetIndex && a.target_type === 'source_override'
      && a.target_ref === `R${row}C${col}`);
    if (overrideAnn) {
      const n = Number(overrideAnn.value);
      return Number.isFinite(n) ? { raw: n, type: 'n' } : { raw: overrideAnn.value, type: 's' };
    }
    const cell = (sheet?.cells || []).find(x => x.r === row && x.c === col);
    if (!cell) return { raw: null, type: 'b' };
    if (typeof cell.raw === 'number' && cell.type !== 'd') return { raw: cell.raw, type: 'n' };
    return { raw: cell.raw, type: cell.type || 's' };
  }, [sheet, sourceColCount, calcColBase, calcCells, annotations, sheetIndex]);

  // ── Calc cell state helpers ──────────────────────────────────────
  const enableCalc = () => {
    setCalcEnabled(true);
    setPanel(PANEL.NONE);   // §AB — one auxiliary panel at a time
  };
  const disableCalc = () => {
    setCalcEnabled(false);
    setActiveCalcCell(null);
    setCalcDraft('');
  };
  const clearCalc = () => {
    setCalcCells({});
    setActiveCalcCell(null);
    setCalcDraft('');
    toast.success('Quick Calc cleared');
  };

  const onCalcActivate = (r, cIdx) => {
    // Persist any pending draft first
    if (activeCalcCell && (activeCalcCell.r !== r || activeCalcCell.c !== cIdx)) {
      commitCalcCell(activeCalcCell.r, activeCalcCell.c, calcDraft);
    }
    const existing = calcCells[`R${r}C${cIdx}`];
    setActiveCalcCell({ r, c: cIdx });
    setCalcDraft(existing?.formula ?? '');
  };
  const commitCalcCell = (r, cIdx, formulaText) => {
    const key = `R${r}C${cIdx}`;
    if (formulaText === '' || formulaText == null) {
      setCalcCells(prev => {
        const next = { ...prev }; delete next[key]; return next;
      });
    } else {
      // Compute display now
      const rv = evalFormula(formulaText, cellLookup);
      const fmt = calcCells[key]?.fmt || 'num';
      const display = rv.error ? rv.error : formatCalc(rv.value, fmt);
      setCalcCells(prev => ({ ...prev, [key]: { formula: formulaText, fmt, display, value: rv.value, error: rv.error } }));
    }
    setActiveCalcCell(null);
    setCalcDraft('');
  };
  const onCalcFormulaChange = (draft) => setCalcDraft(draft);

  // Formula-pick: called by WorkbookRenderer when a workbook source cell is
  // mouse-down'd while a calc cell is active with a formula draft.
  const onWorkbookCellClickForFormula = (r, c, isDrag = false) => {
    if (!activeCalcCell) return false;
    const trimmed = calcDraft.trim();
    if (!trimmed.startsWith('=')) return false;   // let normal selection happen
    const a1 = `${colIndexToLetters(c)}${r}`;
    // If drag (buttons held), extend last ref into a range
    let next = calcDraft;
    if (isDrag) {
      // Replace trailing "REF" or "REF:REF" with "REF:newA1"
      const m = /([A-Z]+\d+)(:[A-Z]+\d+)?$/.exec(next);
      if (m) next = next.slice(0, m.index) + m[1] + ':' + a1;
      else   next = next + a1;
    } else {
      next = next + a1;
    }
    setCalcDraft(next);
    return true;
  };

  // Formula-bar value for active cell
  const formulaBarValue = activeCalcCell ? calcDraft : (
    // Show a static formula for currently focused source cell? No — bar is for calc only.
    ''
  );
  const changeActiveFormat = (fmt) => {
    if (!activeCalcCell) return;
    const key = `R${activeCalcCell.r}C${activeCalcCell.c}`;
    setCalcCells(prev => {
      const existing = prev[key];
      const formula = existing?.formula ?? calcDraft;
      const rv = evalFormula(formula, cellLookup);
      const display = rv.error ? rv.error : formatCalc(rv.value, fmt);
      return { ...prev, [key]: { formula, fmt, display, value: rv.value, error: rv.error } };
    });
  };

  // Recompute all calc cells whenever the underlying data changes.
  useEffect(() => {
    if (!Object.keys(calcCells).length) return;
    setCalcCells(prev => {
      const next = {};
      for (const [k, v] of Object.entries(prev)) {
        const rv = evalFormula(v.formula, cellLookup);
        next[k] = { ...v, display: rv.error ? rv.error : formatCalc(rv.value, v.fmt || 'num'),
                     value: rv.value, error: rv.error };
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotations, sheet]);

  const displayedAnnotations = showHighlights
    ? annotations
    : annotations.filter((a) => a.remark && !a.highlight && !a.color_scale && !a.custom_fill);

  const hasSelection = useMemo(() =>
    (selection.ranges?.length || 0) + (selection.cells?.length || 0) + (selection.cols?.length || 0) > 0,
  [selection]);

  const openDiscussForSelection = () => {
    if (!hasSelection && !pendingContext) return toast.error('Select a cell first');
    setPanel(PANEL.NOTES);
  };

  if (!review || !sheet) {
    return <div className="fixed inset-0 flex items-center justify-center bg-[#0f1512] text-white">Loading review…</div>;
  }

  const rightPanelOpen = panel !== PANEL.NONE || alongside;
  const workbookFlex = alongside ? '1 1 50%' : (rightPanelOpen ? '1 1 65%' : '1 1 100%');

  const renderRightPanel = () => {
    if (panel === PANEL.NOTES) {
      return <MeetingNotesPanel reviewId={id} sessionId={session?.id}
                                  review={review}
                                  pendingContext={pendingContext}
                                  onClose={() => setPanel(PANEL.NONE)}/>;
    }
    if (panel === PANEL.PREV_ACTIONS) {
      return <PreviousActionsPanel reviewId={id}
                                    onClose={() => setPanel(PANEL.NONE)}/>;
    }
    if (panel === PANEL.RELATED) {
      return <RelatedFilesPanel reviewId={id}
                                 onOpenAlongside={(rf) => { setAlongside(rf); setPanel(PANEL.NONE); }}
                                 onClose={() => setPanel(PANEL.NONE)}/>;
    }
    return null;
  };

  return (
    <div className="fixed inset-0 flex flex-col bg-[#0f1512]" data-testid="review-presentation">
      <div className="flex items-center justify-between gap-3 px-4 py-2 bg-[color:var(--color-primary)] text-white shadow" data-testid="presentation-bar">
        <div className="min-w-0">
          <h1 className="text-base font-semibold truncate" data-testid="presentation-title">{review.title}</h1>
          <p className="text-[11px] opacity-80 truncate">
            {review.outlet_name || review.portfolio_name} · {review.period_label || review.period_type}
            {review.status === 'reviewed' && <> · <span className="uppercase tracking-wider">REVIEWED</span></>}
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <ToolbarButton onClick={() => setPanel(p => p === PANEL.PREV_ACTIONS ? PANEL.NONE : PANEL.PREV_ACTIONS)}
                          active={panel === PANEL.PREV_ACTIONS} testid="pt-previous-actions"
                          icon={<History className="w-3.5 h-3.5"/>}
                          badge={prevActionsCount > 0 ? prevActionsCount : null}>Previous Actions</ToolbarButton>
          <ToolbarButton onClick={() => calcEnabled ? disableCalc() : enableCalc()}
                          active={calcEnabled} testid="pt-quick-calc"
                          icon={<Calculator className="w-3.5 h-3.5"/>}>Quick Calc</ToolbarButton>
          <ToolbarButton onClick={() => { setPanel(p => p === PANEL.NOTES ? PANEL.NONE : PANEL.NOTES); if (!calcEnabled) {} }}
                          active={panel === PANEL.NOTES} testid="pt-meeting-notes"
                          icon={<MessageSquare className="w-3.5 h-3.5"/>}>Meeting Notes</ToolbarButton>
          <ToolbarButton onClick={() => setPanel(p => p === PANEL.RELATED ? PANEL.NONE : PANEL.RELATED)}
                          active={panel === PANEL.RELATED} testid="pt-related-files"
                          icon={<Paperclip className="w-3.5 h-3.5"/>}>Related Files</ToolbarButton>
          <div className="w-px h-5 bg-white/20 mx-1"/>
          <ToolbarButton onClick={() => setShowRemarks(v => !v)} testid="presentation-toggle-remarks"
                          icon={showRemarks ? <Eye className="w-3.5 h-3.5"/> : <EyeOff className="w-3.5 h-3.5"/>}>Remarks</ToolbarButton>
          <ToolbarButton onClick={() => setShowHighlights(v => !v)} testid="presentation-toggle-highlights"
                          icon={showHighlights ? <Eye className="w-3.5 h-3.5"/> : <EyeOff className="w-3.5 h-3.5"/>}>Highlights</ToolbarButton>
          <button onClick={toggleFullscreen} data-testid="presentation-fullscreen"
                  className="h-8 w-8 rounded-md bg-white/10 hover:bg-white/20 inline-flex items-center justify-center">
            {isFullscreen ? <Minimize2 className="w-4 h-4"/> : <Maximize2 className="w-4 h-4"/>}
          </button>
          <div className="w-px h-5 bg-white/20 mx-1"/>
          {review.status !== 'reviewed' && (
            <Button size="sm" onClick={() => setShowComplete(true)} data-testid="pt-complete-review"
                    className="h-8 px-3 text-xs bg-white text-[color:var(--color-primary)] hover:bg-white/90 gap-1">
              <CheckCircle className="w-3.5 h-3.5"/> Complete Review
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={exit} data-testid="presentation-exit"
                  className="text-white hover:bg-white/20 h-8 px-2.5 gap-1 text-xs">
            <X className="w-4 h-4"/> Exit
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-1 px-3 py-1.5 bg-[#1A2421] overflow-x-auto">
        {(review.sheets || []).map((s) => (
          <button key={s.sheet_index} onClick={() => setSheetIndex(s.sheet_index)}
                  data-testid={`presentation-sheet-${s.sheet_index}`}
                  className={`px-2.5 py-1 text-[11px] whitespace-nowrap rounded ${
                    sheetIndex === s.sheet_index
                      ? 'bg-white text-[color:var(--color-primary)]'
                      : 'text-white/70 hover:bg-white/10'}`}>{s.sheet_name}</button>
        ))}
        {hasSelection && (
          <button onClick={openDiscussForSelection} data-testid="pt-discuss-selection"
                  className="ml-auto px-2.5 py-1 text-[11px] whitespace-nowrap rounded bg-[color:var(--color-primary)]/70 text-white hover:bg-[color:var(--color-primary)]/90 inline-flex items-center gap-1">
            <MessageCircle className="w-3 h-3"/>
            {selection.ranges?.length === 1 && selection.ranges[0].r1 === selection.ranges[0].r2 && selection.ranges[0].c1 === selection.ranges[0].c2 && !selection.cells?.length && !selection.cols?.length
              ? 'Discuss this cell' : 'Discuss selection'}
          </button>
        )}
      </div>

      {/* Formula bar (only when Quick Calc is enabled) */}
      {calcEnabled && (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-[#FDF6D6] border-b border-[#F0B014]/40"
             data-testid="qc-formula-bar">
          <span className="text-[10px] uppercase tracking-wider text-[#7A5000] font-bold">Quick Calc</span>
          <span className="text-[11px] text-[#5C4200] font-mono">
            {activeCalcCell
              ? `${colIndexToLetters(sourceColCount + activeCalcCell.c + 1)}${activeCalcCell.r}`
              : '—'}
          </span>
          <span className="text-[11px] text-[#7A5000] italic mx-1">fx</span>
          <input value={formulaBarValue} readOnly={!activeCalcCell}
                 onChange={(e) => setCalcDraft(e.target.value)}
                 onKeyDown={(e) => {
                   if (e.key === 'Enter' && activeCalcCell) {
                     e.preventDefault();
                     commitCalcCell(activeCalcCell.r, activeCalcCell.c, calcDraft);
                   }
                 }}
                 data-testid="qc-formula-input"
                 placeholder={activeCalcCell ? 'Type = and click Review cells to build the formula' : 'Click a calc cell (yellow columns) to begin'}
                 className="flex-1 h-7 text-xs px-2 border border-[#F0B014]/40 rounded bg-white outline-none focus:border-[#F0B014]"/>
          {activeCalcCell && (
            <div className="flex items-center gap-1">
              {(['num', 'cur', 'pct']).map(f => {
                const cur = calcCells[`R${activeCalcCell.r}C${activeCalcCell.c}`];
                const isActive = (cur?.fmt || 'num') === f;
                return (
                  <button key={f} onClick={() => changeActiveFormat(f)}
                          data-testid={`qc-fmt-${f}`}
                          className={`px-1.5 py-0.5 text-[10px] rounded border ${
                            isActive ? 'bg-[#F0B014] text-white border-[#F0B014]'
                                     : 'text-[#7A5000] border-[#F0B014]/40 hover:bg-[#F0B014]/10'}`}>
                    {FMT_LABELS[f]}
                  </button>
                );
              })}
            </div>
          )}
          <Button size="sm" variant="outline" onClick={clearCalc} data-testid="qc-clear"
                  className="h-7 text-[10px] gap-1 border-[#F0B014]/40 text-[#7A5000]">
            <Trash2 className="w-3 h-3"/> Clear
          </Button>
        </div>
      )}

      <div className="flex-1 min-h-0 flex bg-white">
        <div style={{ flex: workbookFlex, minWidth: 0 }} className="border-r border-[#E7EBE6] bg-white">
          <WorkbookRenderer
            sheet={sheet}
            styles={review.styles || []}
            annotations={displayedAnnotations}
            selection={selection}
            onSelectionChange={setSelection}
            showRemarks={showRemarks}
            editable presentation compact
            calcColumns={calcEnabled ? CALC_COLUMNS : 0}
            calcCells={calcCells}
            activeCalcCell={activeCalcCell}
            calcDraft={calcDraft}
            onCalcActivate={onCalcActivate}
            onCalcFormulaChange={onCalcFormulaChange}
            onCalcCommit={commitCalcCell}
            onWorkbookCellClickForFormula={onWorkbookCellClickForFormula}/>
        </div>
        {alongside && (
          <div style={{ flex: '1 1 50%', minWidth: 0 }} className="border-r border-[#E7EBE6] bg-white">
            <SideBySideRelatedView reviewId={id} relatedFile={alongside}
                                     onClose={() => setAlongside(null)}/>
          </div>
        )}
        {rightPanelOpen && !alongside && (
          <div style={{ width: 400, minWidth: 340 }} className="bg-white">
            {renderRightPanel()}
          </div>
        )}
        {alongside && rightPanelOpen && (
          <div style={{ width: 340, minWidth: 280 }} className="bg-white border-l border-[#E7EBE6]">
            {renderRightPanel()}
          </div>
        )}
      </div>

      {showComplete && (
        <CompleteReviewDialog reviewId={id}
                                onCancel={() => setShowComplete(false)}
                                onCompleted={() => { setShowComplete(false); setReview(r => r ? { ...r, status: 'reviewed' } : r); }}/>
      )}
    </div>
  );
}

function ToolbarButton({ onClick, active, testid, icon, badge, children }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`h-8 px-2 rounded-md text-xs font-medium inline-flex items-center gap-1 relative ${
              active ? 'bg-white text-[color:var(--color-primary)]' : 'bg-white/10 text-white hover:bg-white/20'}`}>
      {icon}
      <span className="hidden md:inline">{children}</span>
      {badge != null && (
        <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-amber-400 text-black text-[9px] font-bold flex items-center justify-center">
          {badge}
        </span>
      )}
    </button>
  );
}
